import { useEffect, useState } from 'react';
import { toast } from '../../../../../../ui/toast';
import { useCreateJobRequisitionMutation } from '../../../../../../utils/api_service/jobOpeningsApi';
import useDebouncedEmployeeSearch from '../../../../../../utils/helpers/AsyncSelect/EmployeeGet';
import { sanitizeJobRequisition } from '../../../../../../utils/helpers/jobRequisitionUtils';
import { EmployeeUser } from '../../../../../../utils/types/admin_types';
import {
  JobDescription,
  JobRequisition,
  applicantScopeType,
} from '../../../../../../utils/types/JobOpenings';
import { SelectOptions } from '../../../../../../utils/types/react_select_types';

const jobRequisitionTypes: SelectOptions[] = [
  {
    label: 'New Role',
    value: 'New Role',
  },
  {
    label: 'Replacement',
    value: 'Replacement',
  },
];

const applicantTypes: SelectOptions[] = [
  {
    label: 'External',
    value: 'External',
  },
  {
    label: 'Internal',
    value: 'Internal',
  },
];

interface IUseJobRequisition {
  jobRequisition: JobRequisition;
  setJobRequisition: React.Dispatch<React.SetStateAction<JobRequisition>>;
  jobDescription?: JobDescription;
  setJobDescription?: React.Dispatch<React.SetStateAction<JobDescription>>;
  employees: EmployeeUser[];
  defaultHiringManager: SelectOptions[] | undefined;
  defaultInternalRecruiter: SelectOptions[] | undefined;
  setDefaultHiringManager: React.Dispatch<
    React.SetStateAction<SelectOptions[] | undefined>
  >;
  setDefaultInternalRecruiter: React.Dispatch<
    React.SetStateAction<SelectOptions[] | undefined>
  >;
  completedElement: () => void;
  editable: boolean;
}

export const useJobRequisition = (props: IUseJobRequisition) => {
  const {
    jobRequisition,
    setJobRequisition,
    jobDescription,
    setJobDescription,
    employees,
    setDefaultInternalRecruiter,
    setDefaultHiringManager,
    completedElement,
  } = props;
  const [createJobRequisitionMutation, { isLoading: isMutationLoading }] =
    useCreateJobRequisitionMutation();
  const [loading, setLoading] = useState(false);
  const [date, setDate] = useState({
    startDate: jobRequisition.deadline
      ? new Date(Number(jobRequisition.deadline) * 1000)
      : null,
    endDate: jobRequisition.deadline
      ? new Date(Number(jobRequisition.deadline) * 1000)
      : null,
  });
  const [tagInput, setTagInput] = useState('');
  const { loadOptionsDebounced } = useDebouncedEmployeeSearch();
  const handleTagKeyDown = (e: React.KeyboardEvent<HTMLInputElement>) => {
    if ((e.key === 'Enter' || e.key === 'Tab') && tagInput.trim() !== '') {
      setJobRequisition((prev) => ({
        ...prev,
        tags: [...(prev.tags ?? []), tagInput.trim()],
      }));
      setTagInput('');
      e.preventDefault();
    }
  };
  const removeTag = (index: number) => {
    setJobRequisition((prev) => ({
      ...prev,
      tags: prev.tags ? prev.tags.filter((_, i) => i !== index) : [],
    }));
  };

  // Existing handlers...
  const handleChange = (
    selectedOptions: SelectOptions[] | SelectOptions,
    { name }: { name: string },
  ) => {
    if (name === 'applicantScope') {
      const optionsArray = Array.isArray(selectedOptions)
        ? selectedOptions
        : [selectedOptions];
      const updatedValues = optionsArray.map(
        (item) => item.value,
      ) as applicantScopeType[];
      setJobRequisition((prevState) => ({
        ...prevState,
        applicantScope: updatedValues,
      }));
    } else if (name === 'jobRequisitionType') {
      const selectedValue = !Array.isArray(selectedOptions)
        ? selectedOptions.value
        : selectedOptions.length > 0
          ? selectedOptions[0].value
          : null;
      if (selectedValue === 'Replacement' || selectedValue === 'New Role') {
        setJobRequisition((prevState) => ({
          ...prevState,
          jobRequisitionType: selectedValue,
        }));
      }
    }
  };

  const defaultEmployeeUserOptions = employees.map((employee) => ({
    value: employee.id,
    label: `${employee.user.firstName} ${employee.user.lastName}`,
  }));

  const handleEmployeeChange = (
    selectedOption: SelectOptions[],
    { name }: { name: string },
  ) => {
    if (name === 'internalRecruiter') {
      setDefaultInternalRecruiter(selectedOption);
    } else if (name === 'hiringManager') {
      setDefaultHiringManager(selectedOption);
    } else {
      return;
    }

    // Ensure we extract only string IDs
    const stringList = selectedOption
      .map((item) => {
        if (typeof item.value === 'string') {
          return item.value;
        }
        // Handle any unexpected object structure
        const value = item.value as any;
        return value?.id || value?.toString?.() || '';
      })
      .filter(Boolean);

    setJobRequisition({
      ...jobRequisition,
      [name]: stringList,
    });
  };

  const changeDate = (timeRange: any) => {
    // Convert the date string to Unix timestamp (seconds)
    const dateString = timeRange.startDate as string;
    const timestamp = Math.floor(new Date(dateString).getTime() / 1000);

    setJobRequisition({
      ...jobRequisition,
      deadline: timestamp,
    });
    setDate(timeRange);
  };

  const submitJobRequisition = async () => {
    // Validate that jobOpeningId is present before submitting
    if (!jobRequisition.jobOpeningId || jobRequisition.jobOpeningId.trim() === '') {
      toast.error('Job Opening ID is required. Please ensure you\'re creating a new job opening first.', {
        position: 'bottom-center',
      });
      return;
    }

    setLoading(true);
    try {
      const sanitizedJobRequisition = sanitizeJobRequisition(jobRequisition);
      const data = await createJobRequisitionMutation(sanitizedJobRequisition).unwrap();
      setJobRequisition({
        ...jobRequisition,
        id: data.id,
      });
      toast.success('Job Requisition Saved Successfully', {
        position: 'bottom-center',
      });
      completedElement();
    } catch (error: any) {
      console.error('Error submitting job requisition:', error);
      const errorMessage =
        error?.data?.error ||
        error?.error ||
        error?.message ||
        'Error Submitting Job Requisition';
      const isJobOpeningIdError =
        errorMessage.includes('jobOpeningId') ||
        error?.data?.data?.includes?.('jobOpeningId');

      if (isJobOpeningIdError) {
        toast.error('Job Opening ID is missing or invalid. Please reload and try again.', {
          position: 'bottom-center',
        });
      } else {
        toast.error(errorMessage, {
          position: 'bottom-center',
        });
      }
    } finally {
      setLoading(false);
    }
  };

  return {
    loading: loading || isMutationLoading,
    submitJobRequisition,
    changeDate,
    handleChange,
    jobRequisitionTypes,
    applicantTypes,
    defaultEmployeeUserOptions,
    handleEmployeeChange,
    loadOptionsDebounced,
    date,
    tagInput,
    removeTag,
    handleTagKeyDown,
    setTagInput,
    setDate,
  };
};
